import React from 'react';
import { motion } from 'framer-motion';
import { Leaf, UserCheck, Heart, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const approaches = [
  {
    title: 'Holistic Wellness',
    description: 'A complete approach to a healthier body, calmer mind, and balanced life.',
    icon: Leaf,
    link: '/services'
  },
  {
    title: 'Expert Practitioners',
    description: 'Learn from certified and experienced yoga and wellness professionals.',
    icon: UserCheck,
    link: '/classes'
  },
  {
    title: 'Mindful Living',
    description: 'Practical tools and routines to help you stay centered, focused, and energized.',
    icon: Heart,
    link: '/luxury-wellness-retreats'
  }
];

export default function ApproachSection() {
  return (
    <section className="relative bg-white py-14 sm:py-20 overflow-hidden">


      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 relative z-10">
        {/* Header Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5 }}
          className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-10 sm:mb-14"
        >
          <div className="max-w-[620px]">
            <span className="font-mono text-[12px] sm:text-[12.5px] tracking-[0.16em] uppercase text-[#9E7B3B] font-bold inline-flex items-center gap-2 before:content-[''] before:w-5 before:h-[1.5px] before:bg-[#9E7B3B] mb-3">
              OUR APPROACH TO WELLBEING
            </span>
            <h2 className="font-serif font-bold text-3xl sm:text-4xl lg:text-[42px] text-[#15302B] leading-[1.15]">
              Rooted in tradition. Designed for today.
            </h2>
          </div>

          <p className="text-[#5C6E66] text-sm sm:text-[15.5px] leading-relaxed max-w-[480px]">
            We bring together the timeless wisdom of Ayurveda and holistic practices with modern wellness science to create meaningful, lasting change.
          </p>
        </motion.div>

        {/* 3 Approach Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {approaches.map((item, idx) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: idx * 0.1 }}
              >
                <Link
                  to={item.link}
                  className="group rounded-2xl border border-[#EBE4D5] bg-white/85 backdrop-blur-sm p-6 sm:p-7 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex items-center justify-between gap-4 block"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#E5F3EE] text-[#2C6B56] flex items-center justify-center shrink-0 shadow-inner group-hover:scale-105 transition-transform">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-serif font-bold text-[18px] text-[#15302B] group-hover:text-[#2C6B56] transition-colors mb-1.5 leading-snug">
                        {item.title}
                      </h3>
                      <p className="text-[13px] text-[#6A7A75] leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>

                  <div className="w-9 h-9 rounded-full bg-[#EFE6D4] text-[#8C6D34] flex items-center justify-center shrink-0 shadow-sm group-hover:bg-[#E2D5BE] group-hover:scale-105 transition-all">
                    <ArrowRight className="w-4 h-4 stroke-[2.5]" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
